import React, { useCallback } from "react";
import FormField from "@/components/forms/FormField";
import { BUTTONS, LABELS, PLACEHOLDERS } from "@/utils/constants/appConstants/commonConstants";
import AppButton from "@/components/buttons/AppButton";
import { resetPassword } from "@/services/auth.service";
import { isEmail } from "@/utils/functions/helperFunctions";
import { INVALID_CAPTCHA_TOKEN, INVALID_EMAIL_ADDRESS } from "@/utils/constants/messages";
import { useNavigate } from "react-router-dom";
import ForgotPasswordSuccess from "@/pages/auth/ForgotPasswordSuccess";
import { getemail_address } from "@/utils/functions/TokenFunction";
import { IS_CAPTCHA_ENABLED } from "@/constants/allconstants";
import { useCaptcha } from "@/utils/hooks/useRecaptcha";
import { ReCaptchaV2 } from "@/utils/hooks/RecaptchaV2";
import { ERROR_CODES } from "@/utils/constants/constants";
import { Button } from "@/components/ui/button";

interface ForgotPasswordFormProps {
    show?: boolean;
    setShow?: (show: boolean) => void;
    title?: string;
    onSuccessChange?: (success: boolean) => void;
}

interface CaptchaData {
    captcha_token: string;
    captcha_type: 'V3' | 'V2';
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = (props) => {
    const [email, setEmail] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    const [errMsg, setErrMsg] = React.useState("");
    const [showSuccess, setShowSuccess] = React.useState(false);
    const navigate = useNavigate();
    const { executeCaptcha, setCaptchaV2Token, captchaV3enabled, setCaptchaV3enabled, captchaV2Token } = useCaptcha({ onV2Failure: () => setErrMsg(INVALID_CAPTCHA_TOKEN) });

    React.useEffect(() => {
        props.onSuccessChange?.(showSuccess);
    }, [props.onSuccessChange, showSuccess]);

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => { setErrMsg(""); setEmail(e.target.value); };

    const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const handleReset = (data: any) => {
            resetPassword(data, (res: any) => { setIsLoading(false); setShowSuccess(true); }, (err: any) => {
                setIsLoading(false); setErrMsg(err?.message);
                if (err.response?.data?.error_code === ERROR_CODES.CAPTCHA_V3_INVALID_TOKEN_ERROR_CODE || err.response?.data?.error_code === ERROR_CODES.CAPTCHA_NO_TOKEN_ERROR_CODE) setCaptchaV3enabled(false);
                console.log(err);
            });
        };
        if (props?.title !== 'change-password') {
            if (!isEmail(email)) { setErrMsg(INVALID_EMAIL_ADDRESS); return; }
            setIsLoading(true);
            if (IS_CAPTCHA_ENABLED) {
                try { await executeCaptcha('admin_forgot_password_action', (captchaData: CaptchaData) => handleReset({ email_address: email, portal_type: "admin", ...captchaData })); }
                catch (err: any) { setIsLoading(false); setErrMsg(INVALID_CAPTCHA_TOKEN); }
            } else { handleReset({ email_address: email, portal_type: "admin" }); }
        } else {
            setIsLoading(true);
            handleReset({ email_address: getemail_address(), portal_type: "admin" });
        }
    }, [email, isLoading]);

    return !showSuccess ? (
        <form method="POST" id="forgotPasswordForm" autoComplete="off" onSubmit={handleSubmit}>
            {props?.title !== 'change-password' && (
                <div className="mb-4">
                    <label className="text-sm font-medium text-foreground mb-1 block" htmlFor="forgot-password-email-address-input" id="forgot-password-email-address-label">{LABELS.EMAIL}</label>
                    <FormField value={email} onChange={handleEmailChange} type="email" id="forgot-password-email-address-input" placeholder={PLACEHOLDERS.EMAIL} required />
                </div>
            )}
            {errMsg && <p className="text-destructive text-sm mb-3">{errMsg}</p>}
            <div>
                <AppButton disabled={!captchaV3enabled && !captchaV2Token} id="forgot-password-sent-btn" isLoading={isLoading} btnText={props?.title !== 'change-password' ? BUTTONS.SEND_RESET_LINK : BUTTONS.SEND_SET_PASSWORD_LINK} className="btn-lg btn-block btn-primary" type="submit" />
            </div>
            {props?.title !== 'change-password' && (
                <div className="text-center mt-4">
                    <Button variant="link" id="forgot-password-return-to-login-btn" onClick={() => navigate('/')}>
                        {LABELS.RETURN_TO_LOGIN}
                    </Button>
                </div>
            )}
            <div className='mt-2'>{!captchaV3enabled ? <ReCaptchaV2 setToken={setCaptchaV2Token} /> : null}</div>
        </form>
    ) : <ForgotPasswordSuccess title={props?.title} />;
};

export default ForgotPasswordForm;
